export interface GeneratedEmail {
    email: string;
    username: string;
    domain: string;
}

const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const getRandomItem = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

// Popular email domains
const EMAIL_DOMAINS = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'icloud.com',
    'protonmail.com',
    'mail.com',
    'aol.com',
    'yandex.com',
    'gmx.com',
    'zoho.com',
    'live.com',
    'msn.com',
    'rediffmail.com',
    'mail.ru'
];

// Common username patterns
const FIRST_NAMES = [
    'james', 'mary', 'john', 'patricia', 'robert', 'jennifer', 'michael', 'linda',
    'william', 'elizabeth', 'david', 'barbara', 'richard', 'susan', 'joseph', 'jessica',
    'thomas', 'sarah', 'charles', 'karen', 'christopher', 'nancy', 'daniel', 'lisa',
    'matthew', 'betty', 'anthony', 'margaret', 'mark', 'sandra', 'donald', 'ashley',
    'steven', 'kimberly', 'paul', 'emily', 'andrew', 'donna', 'joshua', 'michelle',
    'kenneth', 'dorothy', 'kevin', 'carol', 'brian', 'amanda', 'george', 'melissa',
    'ahmet', 'ayşe', 'mehmet', 'fatma', 'mustafa', 'zeynep', 'ali', 'elif', 'hüseyin',
    'meryem', 'ibrahim', 'hatice', 'ismail', 'emine', 'osman', 'sultan', 'halil',
    'rabia', 'yusuf', 'büşra', 'ömer', 'gül', 'murat', 'şeyma', 'serkan', 'deniz'
];

const LAST_NAMES = [
    'smith', 'johnson', 'williams', 'brown', 'jones', 'garcia', 'miller', 'davis',
    'rodriguez', 'martinez', 'hernandez', 'lopez', 'wilson', 'anderson', 'thomas', 'taylor',
    'moore', 'jackson', 'martin', 'lee', 'thompson', 'white', 'harris', 'sanchez',
    'clark', 'ramirez', 'lewis', 'robinson', 'walker', 'young', 'allen', 'king',
    'wright', 'scott', 'torres', 'nguyen', 'hill', 'flores', 'green', 'adams',
    'yılmaz', 'kaya', 'demir', 'şahin', 'çelik', 'yıldız', 'yıldırım', 'öztürk',
    'aydın', 'özdemir', 'arslan', 'doğan', 'kılıç', 'aslan', 'çetin', 'kara'
];

const NUMBERS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const SPECIAL_CHARS = ['.', '_', '-'];

// Generate random username
function generateUsername(includeNumbers: boolean = true, includeSpecial: boolean = false): string {
    const patterns = [
        // firstname.lastname
        () => `${getRandomItem(FIRST_NAMES)}.${getRandomItem(LAST_NAMES)}`,
        // firstname_lastname
        () => `${getRandomItem(FIRST_NAMES)}_${getRandomItem(LAST_NAMES)}`,
        // firstname-lastname
        () => `${getRandomItem(FIRST_NAMES)}-${getRandomItem(LAST_NAMES)}`,
        // firstname + number
        () => `${getRandomItem(FIRST_NAMES)}${includeNumbers ? getRandomNumber(100, 9999) : ''}`,
        // lastname + number
        () => `${getRandomItem(LAST_NAMES)}${includeNumbers ? getRandomNumber(100, 9999) : ''}`,
        // firstname + lastname + number
        () => `${getRandomItem(FIRST_NAMES)}${getRandomItem(LAST_NAMES)}${includeNumbers ? getRandomNumber(10, 99) : ''}`,
        // random string
        () => {
            const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < getRandomNumber(6, 12); i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        }
    ];

    let username = getRandomItem(patterns)();
    
    // Optionally add special characters
    if (includeSpecial && Math.random() > 0.7) {
        const special = getRandomItem(SPECIAL_CHARS);
        const parts = username.split('');
        const insertPos = Math.floor(Math.random() * (parts.length - 1)) + 1;
        parts.splice(insertPos, 0, special);
        username = parts.join('');
    }

    return username.toLowerCase();
}

export interface EmailGeneratorOptions {
    count: number;
    domain?: string;
    includeNumbers?: boolean;
    includeSpecial?: boolean;
}

export function generateEmails(options: EmailGeneratorOptions): GeneratedEmail[] {
    const {
        count,
        domain,
        includeNumbers = true,
        includeSpecial = false
    } = options;

    const results: GeneratedEmail[] = [];
    const selectedDomain = domain || getRandomItem(EMAIL_DOMAINS);

    for (let i = 0; i < count; i++) {
        const username = generateUsername(includeNumbers, includeSpecial);
        const emailDomain = domain || getRandomItem(EMAIL_DOMAINS);
        
        results.push({
            email: `${username}@${emailDomain}`,
            username,
            domain: emailDomain
        });
    }

    return results;
}

export const AVAILABLE_EMAIL_DOMAINS = EMAIL_DOMAINS.map(domain => ({
    code: domain,
    name: domain
}));
